/**
 * Trunnion AI - Version Compare Modal
 * Full-screen modal wrapping the VersionDiff component.
 * Includes left/right version selectors and renders the diff view.
 */

import { useState, useMemo } from 'react';
import { GitCompare } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { VersionDiff } from './VersionDiff';
import { MOCK_VERSIONS, type WorkflowVersion } from './VersionHistory';

interface VersionCompareModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selected version A (older) */
  initialVersionA?: WorkflowVersion;
  /** Pre-selected version B (newer) */
  initialVersionB?: WorkflowVersion;
}

export function VersionCompareModal({
  open,
  onClose,
  initialVersionA,
  initialVersionB,
}: VersionCompareModalProps) {
  const versions = MOCK_VERSIONS;

  const [leftVersionId, setLeftVersionId] = useState<string>(
    initialVersionA?.id ?? versions[versions.length - 1]?.id ?? '',
  );
  const [rightVersionId, setRightVersionId] = useState<string>(
    initialVersionB?.id ?? versions[0]?.id ?? '',
  );

  // Update when initial versions change
  useState(() => {
    if (initialVersionA) setLeftVersionId(initialVersionA.id);
    if (initialVersionB) setRightVersionId(initialVersionB.id);
  });

  const versionOptions = useMemo(
    () =>
      versions.map((v) => ({
        value: v.id,
        label: `v${v.version} - ${v.message}${v.isCurrent ? ' (current)' : ''}`,
      })),
    [versions],
  );

  const leftVersion = useMemo(
    () => versions.find((v) => v.id === leftVersionId),
    [versions, leftVersionId],
  );

  const rightVersion = useMemo(
    () => versions.find((v) => v.id === rightVersionId),
    [versions, rightVersionId],
  );

  const swapVersions = () => {
    const temp = leftVersionId;
    setLeftVersionId(rightVersionId);
    setRightVersionId(temp);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Version ${leftVersion?.version ?? '?'} vs Version ${rightVersion?.version ?? '?'}`}
      description="Compare changes between two workflow versions"
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Version selectors */}
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Select
              label="Left Version (older)"
              options={versionOptions}
              value={leftVersionId}
              onChange={(e) => setLeftVersionId(e.target.value)}
            />
          </div>

          <Button
            variant="ghost"
            size="sm"
            icon={<GitCompare className="h-4 w-4" />}
            onClick={swapVersions}
            title="Swap versions"
            className="mb-0.5"
          />

          <div className="flex-1">
            <Select
              label="Right Version (newer)"
              options={versionOptions}
              value={rightVersionId}
              onChange={(e) => setRightVersionId(e.target.value)}
            />
          </div>
        </div>

        {/* Same version warning */}
        {leftVersionId === rightVersionId && (
          <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-3">
            <p className="text-sm text-yellow-400">
              Select two different versions to see changes.
            </p>
          </div>
        )}

        {/* Diff view */}
        {leftVersion && rightVersion && leftVersionId !== rightVersionId && (
          <VersionDiff
            versionA={leftVersion.snapshot}
            versionB={rightVersion.snapshot}
            labelA={`v${leftVersion.version}`}
            labelB={`v${rightVersion.version}`}
          />
        )}
      </div>
    </Modal>
  );
}
